import { useCallback, useEffect, useRef } from 'react';
import type { PointerEventHandler, RefObject } from 'react';
import { HARP, HARP_STRINGS } from './bgData';
import { Harp, STRING_COUNT, type Voice } from './harpAudio';

/** How near a press has to land to count as touching a cable. Canvas px. */
const TOUCH = 5;
/** A cable will not sound twice inside this, so a jittery hand is not a tremolo. */
const RETRIGGER_MS = 45;
/** How long a plucked cable stays lit. */
const FLASH_S = 1.1;
/** Alpha of the cable under the cursor, before anyone has plucked it. */
const HOVER_ALPHA = 0.34;

/** Every cable segment, flattened once: the crossing test runs over this. */
interface Seg { i: number; x1: number; y1: number; x2: number; y2: number }
const SEGS: Seg[] = HARP_STRINGS.flatMap((spans, i) =>
  spans.flatMap((span) =>
    span.slice(1).map((p, k) => {
      const q = span[k]!;
      return { i, x1: q[0]!, y1: q[1]!, x2: p[0]!, y2: p[1]! };
    }),
  ),
);

/**
 * Where along A->B the pointer crossed segment S, or null.
 *
 * Parametric, rather than a hit test on where the pointer ended up: a fast
 * sweep arrives as one pointermove a whole fan-width long, and testing only its
 * endpoints would silently skip every cable in between — which is exactly the
 * glissando you were trying to play.
 */
function crossing(ax: number, ay: number, bx: number, by: number, s: Seg): number | null {
  const rx = bx - ax;
  const ry = by - ay;
  const sx = s.x2 - s.x1;
  const sy = s.y2 - s.y1;
  const den = rx * sy - ry * sx;
  if (den === 0) return null; // running along the cable, not across it
  const qx = s.x1 - ax;
  const qy = s.y1 - ay;
  const t = (qx * sy - qy * sx) / den;
  const u = (qx * ry - qy * rx) / den;
  return t >= 0 && t <= 1 && u >= 0 && u <= 1 ? t : null;
}

function distance2(px: number, py: number, s: Seg): number {
  const dx = s.x2 - s.x1;
  const dy = s.y2 - s.y1;
  const len2 = dx * dx + dy * dy;
  const t = len2 ? Math.max(0, Math.min(1, ((px - s.x1) * dx + (py - s.y1) * dy) / len2)) : 0;
  const ex = s.x1 + dx * t - px;
  const ey = s.y1 + dy * t - py;
  return ex * ex + ey * ey;
}

/** The cable nearest a point, or -1 if none is within `TOUCH`. */
function nearest(px: number, py: number): number {
  let best = -1;
  let bestD = TOUCH * TOUCH;
  for (const s of SEGS) {
    const d = distance2(px, py, s);
    if (d < bestD) {
      bestD = d;
      best = s.i;
    }
  }
  return best;
}

export interface HarpHandle {
  /** The flash canvas — mount it directly after `HARP.after` in the layer stack. */
  canvasRef: RefObject<HTMLCanvasElement>;
  /** The press-catcher `<g>`, which must sit *before* the buildings in the hit SVG. */
  groupRef: RefObject<SVGGElement>;
  handlers: {
    onPointerDown: PointerEventHandler<SVGGElement>;
    onPointerMove: PointerEventHandler<SVGGElement>;
    onPointerUp: PointerEventHandler<SVGGElement>;
    onPointerCancel: PointerEventHandler<SVGGElement>;
    onPointerLeave: PointerEventHandler<SVGGElement>;
  };
}

/**
 * Plucking the Ada bridge.
 *
 * None of this goes through React state. A drag fires ~60 pointer moves a
 * second and each one can light a cable; re-rendering the skyline for that is
 * work with nothing to show for it, so the flash is painted straight onto its
 * own canvas from a rAF loop, exactly as Kulčina's display is.
 */
export function useHarp(voice: Voice = 'harp'): HarpHandle {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const groupRef = useRef<SVGGElement>(null);
  const harpRef = useRef<Harp | null>(null);
  /** Per string, the offsets of its pixels in the flash canvas. */
  const pixels = useRef<Uint32Array[] | null>(null);
  const image = useRef<ImageData | null>(null);
  /** performance.now() of the last pluck of each string; 0 means silent. */
  const struck = useRef(new Float64Array(STRING_COUNT));
  const hover = useRef(-1);
  const drag = useRef({ on: false, x: 0, y: 0 });
  /** Context time the next note may start at, so a run cannot double back. */
  const nextFree = useRef(0);
  const raf = useRef(0);
  /** Read at pluck time, so a beacon click does not rebuild the handlers. */
  const voiceRef = useRef(voice);
  useEffect(() => {
    voiceRef.current = voice;
    // Render the new voice while nobody is sweeping. A no-op until the first
    // press has opened the audio context; that press starts it instead.
    harpRef.current?.prepare(voice);
  }, [voice]);

  // The index map doubles as the sprite: one pixel per lit cable pixel, string
  // number carried in ALPHA rather than in a colour channel, because alpha is
  // the one byte no colour profile can rewrite on the way through a canvas.
  useEffect(() => {
    let cancelled = false;
    const img = new Image();
    img.onload = () => {
      if (cancelled) return;
      const c = document.createElement('canvas');
      c.width = HARP.w;
      c.height = HARP.h;
      const g = c.getContext('2d', { willReadFrequently: true });
      if (!g) return;
      g.drawImage(img, 0, 0);
      const d = g.getImageData(0, 0, HARP.w, HARP.h).data;
      const counts = new Uint32Array(STRING_COUNT);
      for (let p = 3; p < d.length; p += 4) {
        const s = (d[p] ?? 0) - 1;
        if (s >= 0 && s < STRING_COUNT) counts[s] = (counts[s] ?? 0) + 1;
      }
      const buckets = Array.from(counts, (n) => new Uint32Array(n));
      const fill = new Uint32Array(STRING_COUNT);
      for (let p = 3, o = 0; p < d.length; p += 4, o++) {
        const s = (d[p] ?? 0) - 1;
        if (s < 0 || s >= STRING_COUNT) continue;
        const at = fill[s] ?? 0;
        buckets[s]![at] = o;
        fill[s] = at + 1;
      }
      pixels.current = buckets;
    };
    img.src = HARP.mask;
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const harp = new Harp();
    harpRef.current = harp;
    return () => {
      cancelAnimationFrame(raf.current);
      harp.dispose();
      harpRef.current = null;
    };
  }, []);

  const paint = useCallback(() => {
    raf.current = 0;
    const canvas = canvasRef.current;
    const px = pixels.current;
    if (!canvas || !px) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    if (!image.current) image.current = ctx.createImageData(HARP.w, HARP.h);
    const data = image.current.data;
    data.fill(0);
    const [r, g, b] = HARP.litRGB;
    const now = performance.now();
    const calm = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    let busy = false;

    for (let i = 0; i < STRING_COUNT; i++) {
      const t0 = struck.current[i] ?? 0;
      const age = t0 ? (now - t0) / 1000 : Infinity;
      let a = 0;
      if (age < FLASH_S) {
        // Decay with a shimmer riding on it: a plucked string is not a lamp
        // being switched off, it is a string that is still moving.
        a = Math.exp(-age / 0.34) * (calm ? 1 : 0.82 + 0.18 * Math.cos(age * 82));
        busy = true;
      } else if (t0) {
        struck.current[i] = 0;
      }
      if (i === hover.current) a = Math.max(a, HOVER_ALPHA);
      if (a <= 0.004) continue;
      const alpha = Math.min(255, Math.round(a * 255));
      for (const o of px[i]!) {
        const p = o * 4;
        data[p] = r;
        data[p + 1] = g;
        data[p + 2] = b;
        data[p + 3] = alpha;
      }
    }
    ctx.putImageData(image.current, 0, 0);
    if (busy) raf.current = requestAnimationFrame(paint);
  }, []);

  const repaint = useCallback(() => {
    if (!raf.current) raf.current = requestAnimationFrame(paint);
  }, [paint]);

  /** Pointer -> canvas coordinates, through the hit SVG's own viewBox. */
  const toCanvas = useCallback((e: { clientX: number; clientY: number }) => {
    const svg = groupRef.current?.ownerSVGElement;
    if (!svg) return null;
    const box = svg.getBoundingClientRect();
    if (!box.width || !box.height) return null;
    const vb = svg.viewBox.baseVal;
    return {
      x: ((e.clientX - box.left) / box.width) * vb.width + vb.x,
      y: ((e.clientY - box.top) / box.height) * vb.height + vb.y,
    };
  }, []);

  const strike = useCallback((i: number, at: number, gain: number, now: number) => {
    if (i < 0 || now - (struck.current[i] ?? 0) < RETRIGGER_MS) return;
    struck.current[i] = now;
    harpRef.current?.pluck(i, at, gain, voiceRef.current);
  }, []);

  const onPointerDown = useCallback<PointerEventHandler<SVGGElement>>(
    (e) => {
      const p = toCanvas(e);
      if (!p) return;
      e.currentTarget.setPointerCapture(e.pointerId);
      drag.current = { on: true, x: p.x, y: p.y };
      const base = harpRef.current?.wake() ?? 0;
      harpRef.current?.prepare(voiceRef.current);
      nextFree.current = base;
      strike(nearest(p.x, p.y), base, 1, performance.now());
      repaint();
    },
    [repaint, strike, toCanvas],
  );

  const onPointerMove = useCallback<PointerEventHandler<SVGGElement>>(
    (e) => {
      if (!drag.current.on) {
        const p = toCanvas(e);
        const near = p ? nearest(p.x, p.y) : -1;
        if (near !== hover.current) {
          hover.current = near;
          groupRef.current?.classList.toggle('is-on', near >= 0);
          repaint();
        }
        return;
      }

      // Browsers batch pointer moves, and the ones they dropped are the middle
      // of the sweep — which is where most of the cables are.
      const native = e.nativeEvent;
      const dropped = native.getCoalescedEvents?.() ?? [];
      const path = dropped.length ? dropped : [native];
      const hits: { i: number; t: number }[] = [];
      let leg = 0;
      for (const step of path) {
        const p = toCanvas(step);
        if (!p) continue;
        const { x, y } = drag.current;
        for (const s of SEGS) {
          const t = crossing(x, y, p.x, p.y, s);
          if (t !== null) hits.push({ i: s.i, t: leg + t });
        }
        drag.current = { on: true, x: p.x, y: p.y };
        leg += 1;
      }
      if (!hits.length) return;

      hits.sort((a, b) => a.t - b.t);
      const now = performance.now();
      const clock = harpRef.current?.wake() ?? 0;
      // Sounded in the order they were crossed, and quieter the more of them
      // there are: a sweep of the whole fan is a run, not a 22-note chord.
      const step = Math.min(0.007, 0.08 / hits.length);
      const gain = 1 / (1 + 0.07 * hits.length);
      // One sweep arrives as several pointermove events, and the last note of
      // one batch is scheduled into the future — so starting the next batch at
      // "now" lays it *under* the note before it and the run stumbles. Carry the
      // queue across events instead, capped so a long sweep cannot drift behind
      // the hand.
      const base = Math.max(clock, Math.min(nextFree.current, clock + 0.06));
      let k = 0;
      for (const h of hits) {
        strike(h.i, base + k * step, gain, now);
        k += 1;
      }
      nextFree.current = base + k * step;
      repaint();
    },
    [repaint, strike, toCanvas],
  );

  const release = useCallback<PointerEventHandler<SVGGElement>>((e) => {
    drag.current.on = false;
    if (e.currentTarget.hasPointerCapture(e.pointerId)) {
      e.currentTarget.releasePointerCapture(e.pointerId);
    }
  }, []);

  const onPointerLeave = useCallback<PointerEventHandler<SVGGElement>>(
    (e) => {
      if (drag.current.on || e.currentTarget.hasPointerCapture(e.pointerId)) return;
      if (hover.current !== -1) {
        hover.current = -1;
        groupRef.current?.classList.remove('is-on');
        repaint();
      }
    },
    [repaint],
  );

  return {
    canvasRef,
    groupRef,
    handlers: {
      onPointerDown,
      onPointerMove,
      onPointerUp: release,
      onPointerCancel: release,
      onPointerLeave,
    },
  };
}
